import {MenuItem} from "primevue/menuitem";
import {AbstractInteractionMode} from "../../Map/interaction-mode/abstract-interaction-mode";
//@ts-ignore
import {Layer, LayerGeometry} from "./layer";
import {DialogServiceMethods} from "primevue/dialogservice";
import {ToastMessageOptions} from "primevue/toast";
import {ConfirmationServiceMethods} from "primevue/confirmationservice";
import {InteractionState} from "../../Map/interaction/abstract-interaction";
import {Store} from "vuex";
import {Feature} from "geojson";

/**
 * Configuration supported by the map controller
 */
export interface MapConfiguration extends mapboxgl.MapboxOptions {
  env: string,
  translatorProvider: (text: string) => string;
  styleProvider: (styleId: string) => Promise<Layer>;
  toastProvider: (toastOptions: ToastMessageOptions) => void;
  emitterProvider: (eventId: string, properties: any) => void;
  forceUpdateProvider: () => void;
  emitToParent: boolean;
  dialogProvider: () => DialogServiceMethods;
  confirmProvider: () => ConfirmationServiceMethods;

  globalContextMenuProvider: (() => MenuItem[]) | undefined;
  featureContextMenuProvider: ((geometry: LayerGeometry) => MenuItem[]) | undefined;
  multiFeatureContextMenuProvider: ((geometries: LayerGeometry[]) => MenuItem[]) | undefined;
  priorityFeatureContextMenuProvider: ((geometry: LayerGeometry) => MenuItem[]) | undefined;
  actionOptionsContextMenuProvider: (() => MenuItem[]) | undefined;
  interactionModeContextMenuProvider: (() => MenuItem[]) | undefined;

  accessToken: string;
  moduleId: string;
  projection: any;

  interactionModes: Array<typeof AbstractInteractionMode>;
  interactionDefaultMode: typeof AbstractInteractionMode;
  interaction: string | undefined;
  interactionMode: (typeof AbstractInteractionMode) | undefined;
  interactionLayer: Layer | undefined;
  interactionGeometries: LayerGeometry[] | undefined;
  interactionOptions: any | undefined;
  tmpGeometries: Feature[] | undefined;
  focusedGeometries: Feature[] | undefined;
  tmpInteractionLayerId: number | undefined;
  fetchFeatureOptions: any | undefined;
  readOnly: boolean | undefined;
  clearFeaturesOnModeChanged: boolean | undefined;
  mapInteractions: [] | undefined;
  drawStyle: {} | undefined;
  keepCurrentPosition: boolean | undefined;

  appendVisibleLayers: [] | undefined;
  mapLayers: Layer[] | undefined;
  mapAddOns: [] | undefined;

  style: string | undefined;
  controls: {[key: string]: mapboxgl.Control[]} | undefined;
  customButtons: MapCustomButtons;
  cursor: string | undefined;
  user: any | undefined;
  iframeProperties: object | undefined;
}

export class MapCustomButtons {
  top: any[];
  topLeft: any[];
  topRight: any[];
  bottom: any[];
  bottomLeft: any[];
  bottomRight: any[];
}

export class MapEvent {
  static MapLoaded: string = 'map-loaded'
  static MapMoveEnd: string = 'map-move-end'
  static ToggleLoadingSpinner: string = 'toggle-loading-spinner'
  static MapViewChange: string = 'map-view-change'
  static MapContextMenu: string = 'map-contextmenu';
  static MapGeoReferenceCompare: string = 'map-georeference-compare';
  static LayerAdding: string = 'layer-adding';
  static LayerAdd: string = 'layer-add';
  static LayerAdded: string = 'layer-added';
  static LayerChanged: string = 'layer-changed';
  static LayerRemoving: string = 'layer-removing';
  static LayerRemove: string = 'layer-remove';
  static LayerMove: string = 'layer-move';
  static LayerReplace: string = 'layer-replace';
  static LayersChange: string = 'layers-change';
  static LayerGeometryCreate: string = 'layer-geometry-create'
  static LayerGeometryCreated: string = 'layer-geometry-created';
  static LayerGeometryUpdate: string = 'layer-geometry-update'
  static LayerGeometryUpdated: string = 'layer-geometry-updated'
  static LayerGeometryIntersect: string = 'layer-geometry-intersect'
  static LayerGeometryMerge: string = 'layer-geometry-merge'
  static LayerGeometryFocus: string = 'layer-geometry-focus'
  static LayerGeometryRemove: string = 'layer-geometry-remove'
  static LayerGeometryRemoved: string = 'layer-geometry-removed'
  static TmpGeometryCreated: string = 'tmp-geometry-created'
  static TmpGeometryUpdated: string = 'tmp-geometry-updated'
  static TmpGeometriesCleared: string = 'tmp-geometries-cleared'
  static TmpGeometryListUpdate: string = 'tmp-geometry-list-update'
  static TmpGeometryListGet: string = 'tmp-geometry-list-get'
  static TmpGeometryList: string = 'tmp-geometry-list'
  static CustomButtonActioned: string = 'custom-button-actioned'
  static PinAdd: string = 'pin-add'
  static PinRemove: string = 'pin-remove'
  static PinCreated: string = 'pin-created'
  static PinChanged: string = 'pin-changed'
  static PinSelected: string = 'pin-selected'
  static TmpPinAdd: string = 'tmp-pin-add'
  static DrawModeInit: string = 'draw-mode-init'
  static DrawModeEnabled: string = 'draw-mode-enabled'
  static InteractionModeEnabled: string = 'draw-mode-set'
  static InteractionModeDisabled: string = 'draw-mode-disabled'
  static SnapModeChange: string = 'snap-mode-change'
  static Tick: string = 'tick'
  static Print: string = 'print';
  static MapFormAction: string = 'map-form-action'
  static ToggleActionOption: string = 'toggle-action-option';
  static InteractionChanged: string = 'interaction-changed';
  static ManualInputDialogClosed: string = 'manual-input-dialog-closed'
  static FeatureFocus: string = 'feature-focus'
  static FeatureList: string = 'feature-list'
  //static EnableDropImport: string = 'enable-drop-import'
  //static DisableDropImport: string = 'disable-drop-import'
  static FocusedGeometryListUpdate: string = 'focused-geometry-list-update'
  static FocusedGeometryListGet: string = 'focused-geometry-list-get'
  static VisibleLayerListGet: string = 'visible-layer-list-get'
  static VisibleLayerList: string = 'visible-layer-list'
  static LayerDropped: string = 'layer-dropped'
  static VisibleLayerListChanged: string = 'visible-layer-list-changed'
  static ToggleMapInfoButton: string = 'toggle-map-info-button'
  static ToggleCustomMapButton: string = 'toggle-custom-map-button'
  static PrintMap: string = 'print-map'
  static PostMessage: string = 'post-message'
  static MapError: string = 'map-error';
}

export class InteractionRevision {
  public state: InteractionState;

  constructor(
    public interaction: string,
    state: InteractionState
  ) {
    this.state = JSON.parse(JSON.stringify(state));
    if (!state.features) {
      return;
    }
    this.state.features = new Map();
    state.features.forEach(feature => {
      this.state.features.set(feature.id.toString(), JSON.parse(JSON.stringify(feature)));
    });
  }
}
